import {AbstractLitElement} from "../abstract-lit.element";
import {customElement} from "lit/decorators/custom-element.js";
import {html, TemplateResult} from "lit";
import {createRef, ref} from "lit/directives/ref.js";
import {when} from "lit/directives/when.js";
import Quill from "quill";
import {property} from "lit/decorators/property.js";
import type {RichTextDocument} from "@o2d/rich-text-editor";
import "@o2d/rich-text-editor";
import type {RteValue} from "../../domain/rte-value.type";
import {RteValueHelper} from "../../helpers/rte-value.helper";
// Review-Lauf 01 (MAJOR, Linse B): `LineBreakBlot` is only registered with
// Quill as a side effect of importing `line-break.blot.ts` — and that import
// previously existed ONLY in `ha-rte.component.ts`. This read-only viewer
// renders the very same Deltas (including `lineBreak` ops) but never
// triggered that registration itself; under route-level code-splitting a
// view could render here without the editor ever having loaded. Side-effect
// import only (no symbol used).
import "../ha-rte/line-break.blot";
// Review-Lauf 01 (MAJOR, Linse B), RTE unification: the RTE unification
// (2026-09-14) removed `ha-rte.component.ts`'s Quill constructor entirely —
// `Quill.register('formats/image', ImageBlot, true)` lived there, and was the
// ONLY place `image.blot.ts` was ever imported in production. Without it, an
// embedded image in a not-yet-migrated Delta silently fell back to Quill's
// plain built-in `formats/image` blot here — losing `ImageBlot`'s
// click-to-fullscreen-preview handler, with no error anywhere. Side-effect
// import only (no symbol used), same pattern as `LineBreakBlot` above.
// Since 2026-09-26 the blot opens that preview here, in this read-only viewer
// (it used to skip it); a migrated document renders through
// `<o2d-rich-text-editor mode="view">` below, whose image clicks
// `ImagePreviewListener` handles — the same preview either way.
import "../ha-rte/image.blot";

/**
 * Read-only rich-text display — kept usable for EITHER shape of `RteValue`
 * (`Delta | RichTextDocument`, see its own doc comment) since the RTE
 * unification (2026-09): a legacy Quill Delta (a row not yet re-saved since
 * then) still renders through the original standalone Quill viewer below; a
 * migrated `RichTextDocument` renders through `@o2d/rich-text-editor`'s own
 * `<o2d-rich-text-editor mode="view">` instead — the SAME view engine
 * `knowledge-rte`/`ha-dialog-rte-view` use. Every direct consumer
 * (`ha-rte-viewer`, `ha-issue-preview`, `ha-tooltip`, `ha-task-list-item`,
 * `abstract-tile.component.ts`) keeps using this ONE tag either way; only
 * what happens inside branches on the value's shape.
 */
@customElement(`ha-quill-viewer`)
export class HaQuillViewerComponent extends AbstractLitElement {

    private _contentRef = createRef<HTMLDivElement>();

    private _viewer: Quill | null = null;

    private _value: RteValue | null = null;
    private _richTextDocument: RichTextDocument | null = null;

    @property({type: Object})
    public get value(): RteValue | null {
        return this._value;
    }

    public set value(value: RteValue | null) {
        if (!value) {
            return;
        }

        if (RteValueHelper.isRichTextDocument(value)) {
            this._value = value;
            this._richTextDocument = value;
            // The `<div>` a live Quill viewer mounts into is only rendered in
            // the OTHER branch (see `render()`) — if this instance is reused
            // (e.g. across list items) and previously held a Delta, that
            // viewer's target node is about to be removed from the DOM.
            // Dropping the reference lets `updated()` mount a fresh one
            // against a fresh `<div>` if the value ever switches back.
            this._viewer = null;
            this.requestUpdate();
            return;
        }

        if (!Array.isArray((value as { ops?: unknown }).ops) || (value as { ops: unknown[] }).ops.length < 1) {
            return;
        }

        this._value = value;
        this._richTextDocument = null;
        if (this._viewer) {
            this._viewer.setContents(value as never);
        } else {
            this.requestUpdate();
        }
    }

    public render(): TemplateResult {
        return when(this._richTextDocument !== null, () => html`
            <o2d-rich-text-editor mode="view" .content=${this._richTextDocument!}></o2d-rich-text-editor>
        `, () => html`
            <div ${ref(this._contentRef)}></div>
        `);
    }

    public updated() {
        if (this._richTextDocument !== null || this._viewer || !this._contentRef.value) {
            return;
        }
        this._viewer = new Quill(this._contentRef.value!, {
            debug: false,
            readOnly: true
        });
        if (this._value) {
            this._viewer.setContents(this._value as never);
        }
    }

}
